import { useMemo } from 'react';
import { useStatsRevenueMonthly } from '../../../hooks/queries/useStats';

interface Props {
  months: 3 | 6 | 12;
}

function formatMonth(d: string): string {
  return new Date(d).toLocaleDateString('fr-FR', { month: 'short' });
}

function formatAmount(n: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

export function RevenueMonthlyChart({ months }: Props) {
  const { data = [], isLoading, error } = useStatsRevenueMonthly();

  const filtered = useMemo(() => data.slice(-months), [data, months]);
  const max = useMemo(
    () => Math.max(1, ...filtered.map((d) => Number(d.ca_ht ?? 0))),
    [filtered],
  );

  if (isLoading) {
    return <p className="text-sm text-cime-text-muted">Chargement…</p>;
  }
  if (error) {
    return <p className="text-sm text-red-600">Erreur de chargement.</p>;
  }
  if (filtered.length === 0 || filtered.every((d) => Number(d.ca_ht ?? 0) === 0)) {
    return <p className="text-sm text-cime-text-muted">Aucune facture émise sur la période.</p>;
  }

  return (
    <div className="w-full">
      <svg viewBox="0 0 400 160" className="w-full h-40">
        {filtered.map((d, i) => {
          const value = Number(d.ca_ht ?? 0);
          const h = (value / max) * 120;
          const x = i * (400 / filtered.length) + 8;
          const w = 400 / filtered.length - 16;
          return (
            <g key={d.month_start}>
              <rect
                x={x}
                y={140 - h}
                width={w}
                height={h}
                fill="var(--cime-primary)"
                rx="2"
              />
              <text
                x={x + w / 2}
                y={155}
                textAnchor="middle"
                className="text-[10px] fill-cime-text-muted"
              >
                {formatMonth(d.month_start ?? '')}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="grid gap-1 text-xs mt-2" style={{ gridTemplateColumns: `repeat(${filtered.length}, 1fr)` }}>
        {filtered.map((d) => (
          <div key={d.month_start} className="text-center text-cime-text-muted tabular-nums">
            {formatAmount(Number(d.ca_ht ?? 0))}
          </div>
        ))}
      </div>
    </div>
  );
}
